import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { ChipConnector } from '~/components/AiSearchChips/ChipConnector/ChipConnector';
import { useLocaleKeys } from '~/i18n';
import { ALLOWED_BILLED_UPON, ALLOWED_DISCOUNT_TYPE, ALLOWED_PRICING_TYPE, ALLOWED_RECURRENCE, ALLOWED_START_ON_TYPE } from '~/types/api/drafts-search';
import { localizeBilledUpon, localizeDiscountType, localizePricingType, localizeRecurrence, localizeStartOnType } from '../services-localize';
import { useSettingsChipTone } from '../settingsChipTone';
import { moveQuantifier, nextQuantifier, presentServiceEntries, setQuantifier } from './service-predicate';
import { ServiceConditionChip } from './ServiceConditionChip/ServiceConditionChip';
import { checkboxesToSet, hasSetValues, isFlattenableCondition, rebuildDecimalField, rebuildDiscountAmount, rebuildDiscountType, rebuildLimitedTimeDiscount, rebuildPriceIncreaseExists, rebuildPriceIncreasePercent, rebuildPriceIncreaseStartOnType, rebuildSetField, setToCheckboxes } from './serviceConditions';
import { decimalEditableValue, decimalFromInput, decimalIsEditable, decimalOperator, decimalValueLabel, parseDecimal } from './serviceDecimal';
import { rebuildServiceNames, selectedServiceNames } from './serviceName';
import { useServiceNameOptions } from './useServiceNameOptions';
export const GetServicesChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const ServicesChips = (param)=>{
    let { testId, services, onChange } = param;
    const { ServicesChipsTestIds } = useMemo(()=>GetServicesChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const { t: commonFilter } = useLocaleKeys('common.filter');
    const { t: common } = useLocaleKeys('common');
    const tone = useSettingsChipTone();
    const serviceOptions = useServiceNameOptions();
    const serviceItems = useMemo(()=>serviceOptions.map((option)=>({
                value: option.name,
                label: option.name
            })), [
        serviceOptions
    ]);
    const checkboxTexts = {
        all: t.chipValueAll(),
        count: (count)=>"(".concat(count, ")"),
        selectAll: commonFilter.selectAll(),
        apply: t.applyButton(),
        is: t.chipOperatorIs(),
        isNot: t.operatorIsNot(),
        search: common.searchPlaceholder()
    };
    const numberRangeTexts = {
        apply: t.applyButton(),
        validationError: t.servicesDecimalValidationError()
    };
    const decimalOperatorTexts = {
        is: t.servicesOperatorIs(),
        isNot: t.servicesOperatorIsNot()
    };
    const recurrenceItems = ALLOWED_RECURRENCE.map((value)=>({
            value,
            label: localizeRecurrence(t, value)
        }));
    const billedUponItems = ALLOWED_BILLED_UPON.map((value)=>({
            value,
            label: localizeBilledUpon(t, value)
        }));
    const pricingTypeItems = ALLOWED_PRICING_TYPE.map((value)=>({
            value,
            label: localizePricingType(t, value)
        }));
    const discountTypeItems = ALLOWED_DISCOUNT_TYPE.map((value)=>({
            value,
            label: localizeDiscountType(t, value)
        }));
    const startOnTypeItems = ALLOWED_START_ON_TYPE.map((value)=>({
            value,
            label: localizeStartOnType(t, value)
        }));
    const toggleQuantifier = (quantifier)=>{
        const target = nextQuantifier(services, quantifier);
        if (target === quantifier) return;
        onChange(moveQuantifier(services, quantifier, target));
    };
    const flattenedChips = (quantifier, predicate)=>{
        const apply = (next)=>onChange(setQuantifier(services, quantifier, next));
        const chipTestId = (field)=>ServicesChipsTestIds.Chip(quantifier, field);
        const chips = [];
        const setChip = (field, fieldLabel, items, biPrefix)=>{
            const set = predicate[field];
            if (!hasSetValues(set)) return;
            const { selected, operator } = setToCheckboxes(set);
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: chipTestId(field),
                fieldLabel: fieldLabel,
                items: items,
                selected: selected,
                operator: operator,
                tone: tone,
                onApply: (next)=>apply(rebuildSetField(predicate, field, checkboxesToSet(next))),
                onRemove: ()=>apply(rebuildSetField(predicate, field, undefined)),
                biPrefix: biPrefix,
                texts: checkboxTexts
            }, field));
        };
        const decimalChip = (field, fieldLabel, biPrefix)=>{
            const range = predicate[field];
            if (range == null) return;
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "numberRange",
                testId: chipTestId(field),
                fieldLabel: fieldLabel,
                value: decimalEditableValue(range),
                valueLabel: decimalValueLabel(range),
                operator: decimalOperator(range, (next)=>apply(rebuildDecimalField(predicate, field, next)), decimalOperatorTexts),
                tone: tone,
                editable: decimalIsEditable(range),
                validate: (input)=>parseDecimal(input),
                onApply: (input)=>apply(rebuildDecimalField(predicate, field, decimalFromInput(input, range.neq != null))),
                onRemove: ()=>apply(rebuildDecimalField(predicate, field, undefined)),
                biPrefix: biPrefix,
                texts: numberRangeTexts
            }, field));
        };
        const names = selectedServiceNames(predicate);
        if (names.length > 0) {
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: chipTestId('name'),
                fieldLabel: t.servicesNameLabel(),
                items: serviceItems,
                selected: names,
                operator: "include",
                verb: "is",
                searchable: true,
                valueLabel: names.join(', ') || undefined,
                tone: tone,
                onApply: (param)=>{
                    let { selected } = param;
                    return apply(rebuildServiceNames(predicate, selected));
                },
                onRemove: ()=>apply(rebuildServiceNames(predicate, [])),
                biPrefix: "drafts_ai_search_chip_service_name",
                texts: checkboxTexts
            }, "name"));
        }
        setChip('billedUpon', t.servicesBilledUponLabel(), billedUponItems, 'drafts_ai_search_chip_service_billed_upon');
        setChip('recurrence', t.servicesRecurrenceLabel(), recurrenceItems, 'drafts_ai_search_chip_service_recurrence');
        setChip('pricingType', t.servicesPricingTypeLabel(), pricingTypeItems, 'drafts_ai_search_chip_service_pricing');
        decimalChip('priceMin', t.servicesPriceMinLabel(), 'drafts_ai_search_chip_service_price_min');
        decimalChip('priceMax', t.servicesPriceMaxLabel(), 'drafts_ai_search_chip_service_price_max');
        decimalChip('cap', t.servicesCapLabel(), 'drafts_ai_search_chip_service_cap');
        const discount = predicate.discount;
        if (discount != null && hasSetValues(discount.type)) {
            const { selected, operator } = setToCheckboxes(discount.type);
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: chipTestId('discountType'),
                fieldLabel: t.servicesDiscountTypeLabel(),
                items: discountTypeItems,
                selected: selected,
                operator: operator,
                tone: tone,
                onApply: (next)=>apply(rebuildDiscountType(predicate, checkboxesToSet(next))),
                onRemove: ()=>apply(rebuildDiscountType(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_discount_type",
                texts: checkboxTexts
            }, "discountType"));
        }
        if ((discount === null || discount === void 0 ? void 0 : discount.amount) != null) {
            const amount = discount.amount;
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "numberRange",
                testId: chipTestId('discountAmount'),
                fieldLabel: t.servicesDiscountAmountLabel(),
                value: decimalEditableValue(amount),
                valueLabel: decimalValueLabel(amount),
                operator: decimalOperator(amount, (next)=>apply(rebuildDiscountAmount(predicate, next)), decimalOperatorTexts),
                tone: tone,
                editable: decimalIsEditable(amount),
                validate: (input)=>parseDecimal(input),
                onApply: (input)=>apply(rebuildDiscountAmount(predicate, decimalFromInput(input, amount.neq != null))),
                onRemove: ()=>apply(rebuildDiscountAmount(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_discount_amount",
                texts: numberRangeTexts
            }, "discountAmount"));
        }
        const priceIncrease = predicate.priceIncrease;
        if ((priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.percent) != null) {
            const percent = priceIncrease.percent;
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "numberRange",
                testId: chipTestId('priceIncreasePercent'),
                fieldLabel: t.servicesPriceIncreasePercentLabel(),
                value: decimalEditableValue(percent),
                valueLabel: decimalValueLabel(percent),
                operator: decimalOperator(percent, (next)=>apply(rebuildPriceIncreasePercent(predicate, next)), decimalOperatorTexts),
                tone: tone,
                editable: decimalIsEditable(percent),
                validate: (input)=>parseDecimal(input),
                onApply: (input)=>apply(rebuildPriceIncreasePercent(predicate, decimalFromInput(input, percent.neq != null))),
                onRemove: ()=>apply(rebuildPriceIncreasePercent(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_price_increase_percent",
                texts: numberRangeTexts
            }, "priceIncreasePercent"));
        }
        if (priceIncrease != null && hasSetValues(priceIncrease.startOnType)) {
            const { selected, operator } = setToCheckboxes(priceIncrease.startOnType);
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: chipTestId('priceIncreaseStartOnType'),
                fieldLabel: t.servicesPriceIncreaseStartOnLabel(),
                items: startOnTypeItems,
                selected: selected,
                operator: operator,
                tone: tone,
                onApply: (next)=>apply(rebuildPriceIncreaseStartOnType(predicate, checkboxesToSet(next))),
                onRemove: ()=>apply(rebuildPriceIncreaseStartOnType(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_price_increase_start_on",
                texts: checkboxTexts
            }, "priceIncreaseStartOnType"));
        }
        if ((priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.exists) != null) {
            const exists = priceIncrease.exists;
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: chipTestId('priceIncreaseExists'),
                fieldLabel: t.servicesPriceIncreasePercentLabel(),
                valueLabel: exists ? t.servicesPriceIncreaseExistsLabel() : t.servicesPriceIncreaseNoneLabel(),
                tone: tone,
                onToggle: ()=>apply(rebuildPriceIncreaseExists(predicate, !exists)),
                onRemove: ()=>apply(rebuildPriceIncreaseExists(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_price_increase_exists"
            }, "priceIncreaseExists"));
        }
        const limitedTimeDiscountOffer = predicate.limitedTimeDiscountOffer;
        if ((limitedTimeDiscountOffer === null || limitedTimeDiscountOffer === void 0 ? void 0 : limitedTimeDiscountOffer.exists) != null) {
            const exists = limitedTimeDiscountOffer.exists;
            chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: chipTestId('limitedTimeDiscountOffer'),
                fieldLabel: t.servicesConditionalDiscountExistsLabel(),
                valueLabel: exists ? t.servicesConditionalDiscountExistsLabel() : t.servicesConditionalDiscountNoneLabel(),
                tone: tone,
                onToggle: ()=>apply(rebuildLimitedTimeDiscount(predicate, !exists)),
                onRemove: ()=>apply(rebuildLimitedTimeDiscount(predicate, undefined)),
                biPrefix: "drafts_ai_search_chip_service_conditional_discount"
            }, "limitedTimeDiscountOffer"));
        }
        return chips;
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: presentServiceEntries(services).map((param, index)=>{
            let { quantifier, predicate } = param;
            return /*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    index > 0 && /*#__PURE__*/ _jsx(ChipConnector, {
                        testId: ServicesChipsTestIds.AndConnector(index - 1),
                        tone: "and"
                    }),
                    isFlattenableCondition(predicate, quantifier) ? flattenedChips(quantifier, predicate) : /*#__PURE__*/ _jsx(ServiceConditionChip, {
                        testId: ServicesChipsTestIds.SummaryChip(quantifier),
                        quantifier: quantifier,
                        predicate: predicate,
                        onToggleQuantifier: ()=>toggleQuantifier(quantifier),
                        onRemove: ()=>onChange(setQuantifier(services, quantifier, undefined))
                    })
                ]
            }, quantifier);
        })
    });
};
